// @/components/reusable/cookie-banner.jsx

/**
 * Cookie Banner Component
 * 
 * Simple, GDPR-compliant cookie consent banner for analytics.
 * Shows once, user chooses, then only editable in account settings.
 * 
 * Features:
 * - Single analytics preference (defaults to false)
 * - Fixed bottom position, centered
 * - Clean design matching your platform aesthetic
 * - Syncs with account settings toggle
 * - Proper localStorage handling
 * - Fully translatable via ContentSettings
 */

import React, { useState, useEffect } from 'react';
import { Text } from '@/components/reusable/text';
import { Heading } from '@/components/reusable/heading';
import { Button, ButtonGroup } from '@/components/reusable/button';
import { useContentSettings } from '@/hooks/content/useContentSettings';

const CookieBanner = () => {
  const content = useContentSettings();

  // Default fallback text (used when Strapi content is loading or unavailable)
  const defaultText = {
    cookieBannerTitle: "Help us improve your experience!",
    cookieBannerDescription: "We collect anonymized usage data to understand how we can improve the product and help us fix bugs. We do not collect data that directly identifies you. We never use this data for advertising or share it with third parties for marketing purposes. You can turn this on or off at any time.",
    cookieBannerAcceptButtonText: "Accept",
    cookieBannerDenyButtonText: "Deny",
    cookieBannerPrivacyPolicyText: "Privacy Policy"
  };

  const [showBanner, setShowBanner] = useState(false);
  const [blockingDialogOpen, setBlockingDialogOpen] = useState(false);

  // Check if we should show the banner
  useEffect(() => {
    if (typeof window === 'undefined') return; // SSR protection

    // Only show if user hasn't made a choice yet
    const hasConsent = localStorage.getItem('analyticsConsent') !== null;
    setShowBanner(!hasConsent);
  }, []);

  // Hide while the terms gate or an announcement dialog is open. Those are
  // Headless Dialogs: they capture every click outside their panel (a
  // non-dismissible one no-ops it), while this banner's z-50 paints it ABOVE
  // their backdrop — visible, seemingly clickable, but dead. Deferring until
  // both are closed sequences the overlays instead of deadlocking new users
  // (post-launch project + no consent choice yet) on first login.
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const check = () =>
      setBlockingDialogOpen(
        window.__termsGateOpen === true || window.__announcementOpen === true
      );
    check();
    window.addEventListener('blockingOverlayChanged', check);
    return () => window.removeEventListener('blockingOverlayChanged', check);
  }, []);

  // Handle Accept
  const handleAccept = () => {
    if (typeof window === 'undefined') return; // SSR protection

    localStorage.setItem('analyticsConsent', 'true');
    setShowBanner(false);

    // Dispatch event for PostHog integration
    window.dispatchEvent(new CustomEvent('analyticsConsentChanged', {
      detail: { enabled: true, source: 'banner' }
    }));
  };

  // Handle Deny
  const handleDeny = () => {
    if (typeof window === 'undefined') return; // SSR protection

    localStorage.setItem('analyticsConsent', 'false');
    setShowBanner(false);

    // Dispatch event for PostHog integration
    window.dispatchEvent(new CustomEvent('analyticsConsentChanged', {
      detail: { enabled: false, source: 'banner' }
    }));
  };

  // Don't render if banner shouldn't be shown
  if (!showBanner || blockingDialogOpen) return null;

  return (
    <div className="fixed bottom-10 right-10 z-50">
      <div className="mx-auto max-w-lg">
        <div className="bg-white rounded-xl shadow-lg border border-zinc-200 dark:bg-zinc-800 dark:border-zinc-700">
          <div className="bg-primary/5 dark:bg-primary/10 p-6">
            <div className="space-y-4">
              {/* Header - Translatable */}
              <Heading level="3">
                {content?.cookieBannerTitle || defaultText.cookieBannerTitle}
              </Heading>

              {/* Description - Translatable */}
              <Text>
                {content?.cookieBannerDescription || defaultText.cookieBannerDescription}
              </Text>

              {/* Actions */}
              <div className="flex justify-between items-center pt-2.5">
                <ButtonGroup>
                  {/* Accept Button - Translatable */}
                  <Button onClick={handleAccept}>
                    {content?.cookieBannerAcceptButtonText || defaultText.cookieBannerAcceptButtonText}
                  </Button>

                  {/* Deny Button - Translatable */}
                  <Button onClick={handleDeny} variant="outline">
                    {content?.cookieBannerDenyButtonText || defaultText.cookieBannerDenyButtonText}
                  </Button>
                </ButtonGroup>

                {/* Privacy Policy Link - Translatable */}
                <a
                  href="https://supplier-s.io/privacy-policy/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-bold flex gap-1 hover:text-primary transition-colors"
                >
                  {content?.cookieBannerPrivacyPolicyText || defaultText.cookieBannerPrivacyPolicyText}
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={1.5}
                    stroke="currentColor"
                    className="size-6"
                    aria-hidden="true"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M17.25 8.25 21 12m0 0-3.75 3.75M21 12H3" />
                  </svg>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CookieBanner;